import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Award, Upload, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface Certification {
	name: string;
	description: string;
	issueDate: string;
	certificateUrl: string;
	certificateFile?: File;
}

interface CertificationsStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const CertificationsStep = ({
	onNext,
	onBack,
	initialData,
}: CertificationsStepProps) => {
	const [certifications, setCertifications] = useState<Certification[]>(
		initialData?.certifications || [
			{ name: "", description: "", issueDate: "", certificateUrl: "" },
		],
	);
	const [loading, setLoading] = useState(false);
	const [uploadingFiles, setUploadingFiles] = useState<{
		[key: number]: boolean;
	}>({});
	const [uploadSuccess, setUploadSuccess] = useState<{
		[key: number]: boolean;
	}>({});
	const [uploadProgress, setUploadProgress] = useState<{
		[key: number]: string;
	}>({});

	const addCertification = () => {
		setCertifications([
			...certifications,
			{ name: "", description: "", issueDate: "", certificateUrl: "" },
		]);
	};

	const removeCertification = (index: number) => {
		if (certifications.length > 1) {
			setCertifications(certifications.filter((_, i) => i !== index));
		}
	};

	const updateCertification = (
		index: number,
		field: keyof Certification,
		value: string,
	) => {
		const updated = [...certifications];
		updated[index] = { ...updated[index], [field]: value };
		setCertifications(updated);
	};

	const handleFileUpload = async (index: number, file: File) => {
		if (!file) return;

		try {
			setUploadingFiles((prev) => ({ ...prev, [index]: true }));
			setUploadProgress((prev) => ({ ...prev, [index]: "Preparing file..." }));

			// Convert file to base64
			const reader = new FileReader();
			reader.onload = async () => {
				try {
					setUploadProgress((prev) => ({
						...prev,
						[index]: "Converting file...",
					}));
					const base64Data = (reader.result as string).split(",")[1]; // Remove data:...;base64, prefix

					setUploadProgress((prev) => ({
						...prev,
						[index]: "Uploading to server...",
					}));
					const response = await fetch(
						`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-profile-photo`,
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
							},
							body: JSON.stringify({
								file: base64Data,
								filename: `cert-${Date.now()}-${file.name}`,
								content_type: file.type,
								bucket: "certifications",
							}),
						},
					);

					const result = await response.json();

					if (!response.ok) {
						throw new Error(result.error || "Failed to upload certificate");
					}

					setUploadProgress((prev) => ({ ...prev, [index]: "Processing..." }));

					// Update the certification with the file URL
					updateCertification(index, "certificateUrl", result.data.public_url);

					setUploadProgress((prev) => ({ ...prev, [index]: "Complete!" }));

					// Show success feedback
					setUploadSuccess((prev) => ({ ...prev, [index]: true }));

					// Hide success feedback after 3 seconds
					setTimeout(() => {
						setUploadSuccess((prev) => ({ ...prev, [index]: false }));
						setUploadProgress((prev) => ({ ...prev, [index]: "" }));
					}, 3000);
				} catch (error) {
					console.error("Error uploading certificate:", error);
					alert("Failed to upload certificate. Please try again.");
				} finally {
					setUploadingFiles((prev) => ({ ...prev, [index]: false }));
				}
			};
			reader.readAsDataURL(file);
		} catch (error) {
			console.error("Error reading file:", error);
			alert("Error reading file. Please try again.");
			setUploadingFiles((prev) => ({ ...prev, [index]: false }));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const validCertifications = certifications.filter((cert) =>
			cert.name.trim(),
		);
		if (validCertifications.length === 0) {
			alert("Please add at least one certification");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "certifications",
						data: {
							certifications: validCertifications.map((cert) => ({
								name: cert.name,
								description: cert.description,
								issueDate: cert.issueDate,
								certificateUrl: cert.certificateUrl,
							})),
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save certifications");
			}

			onNext({ certifications: validCertifications });
		} catch (error) {
			console.error("Error saving certifications:", error);
			alert("Failed to save certifications. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 9 of 15</span>
					<span className={styles.progressLabel}>Certifications</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "60%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Award className={styles.cardIcon} />
						Certifications
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Add your doula certifications and training credentials to showcase
						your qualifications.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{certifications.map((cert, index) => (
							<motion.div
								key={index}
								className={styles.itemCard}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3, delay: index * 0.1 }}
							>
								{certifications.length > 1 && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => removeCertification(index)}
										className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
									>
										<X className="h-4 w-4" />
									</Button>
								)}
								<div className="space-y-4">
									<Input
										placeholder="Certification Name (e.g., DONA Birth Doula Certification)"
										value={cert.name}
										onChange={(e) =>
											updateCertification(index, "name", e.target.value)
										}
										className="font-karla"
										required
									/>
									<Textarea
										placeholder="Description of certification and training received"
										value={cert.description}
										onChange={(e) =>
											updateCertification(index, "description", e.target.value)
										}
										rows={3}
										className="font-karla"
									/>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className={`block mb-1 ${styles.fieldLabel}`}>
												Issue Date
											</label>
											<Input
												type="date"
												value={cert.issueDate}
												onChange={(e) =>
													updateCertification(index, "issueDate", e.target.value)
												}
												className="font-karla"
											/>
										</div>
										<div>
											<label className={`block mb-1 ${styles.fieldLabel}`}>
												Certificate URL (optional)
											</label>
											<Input
												type="url"
												placeholder="https://..."
												value={cert.certificateUrl}
												onChange={(e) =>
													updateCertification(
														index,
														"certificateUrl",
														e.target.value,
													)
												}
												className="font-karla"
											/>
										</div>
									</div>

									{/* Certificate File Upload */}
									<div>
										<label className={`block mb-2 ${styles.fieldLabel}`}>
											Upload Certificate (optional)
										</label>
										<div className="flex items-center space-x-4">
											<label
												className={
													uploadingFiles[index]
														? "flex items-center space-x-2 cursor-not-allowed px-4 py-2 rounded-lg border bg-newTheme-sky border-newTheme-lightBlue opacity-60"
														: styles.fileUploadLabel
												}
											>
												{uploadingFiles[index] ? (
													<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-newTheme-spruce" />
												) : (
													<Upload className="h-4 w-4 text-newTheme-spruce" />
												)}
												<span className={styles.fileUploadText}>
													{uploadingFiles[index]
														? uploadProgress[index] || "Uploading..."
														: "Choose File"}
												</span>
												{uploadingFiles[index] && (
													<div className="flex items-center space-x-1">
														<div className="w-2 h-2 bg-newTheme-spruce rounded-full animate-pulse" />
														<div className="w-2 h-2 bg-newTheme-spruce rounded-full animate-pulse delay-75" />
														<div className="w-2 h-2 bg-newTheme-spruce rounded-full animate-pulse delay-150" />
													</div>
												)}
												<input
													type="file"
													accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
													onChange={(e) => {
														const file = e.target.files?.[0];
														if (file) handleFileUpload(index, file);
													}}
													className="hidden"
													disabled={uploadingFiles[index]}
												/>
											</label>

											{/* Success feedback */}
											{uploadSuccess[index] && (
												<div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 animate-pulse">
													<svg
														className="h-4 w-4 text-green-600"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M5 13l4 4L19 7"
														/>
													</svg>
													<span className="text-sm font-medium font-karla text-green-600">
														Upload successful!
													</span>
												</div>
											)}

											{cert.certificateUrl && (
												<div className="space-y-3">
													<div className="flex items-center space-x-2 text-sm">
														<div className="flex items-center space-x-2 text-green-600">
															<FileText className="h-4 w-4" />
															<span className="font-karla">Certificate uploaded</span>
														</div>
														<div className="flex items-center space-x-2">
															<a
																href={cert.certificateUrl}
																target="_blank"
																rel="noopener noreferrer"
																className="text-newTheme-spruce hover:opacity-70 underline text-xs font-karla"
															>
																View
															</a>
															<button
																type="button"
																onClick={() =>
																	updateCertification(index, "certificateUrl", "")
																}
																className="text-red-600 hover:text-red-800 text-xs font-karla"
															>
																Remove
															</button>
														</div>
													</div>

													{(cert.certificateUrl.includes(".jpg") ||
														cert.certificateUrl.includes(".jpeg") ||
														cert.certificateUrl.includes(".png")) && (
														<div className="border border-newTheme-lightBlue rounded-lg p-2 bg-newTheme-cotton">
															<img
																src={cert.certificateUrl}
																alt="Certificate preview"
																className="max-w-full h-32 object-contain mx-auto rounded"
																onError={(e) => {
																	(e.target as HTMLElement).style.display = "none";
																}}
															/>
														</div>
													)}
												</div>
											)}
										</div>
										<p className={styles.helperText}>
											Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 10MB)
										</p>
									</div>
								</div>
							</motion.div>
						))}

						<Button
							type="button"
							variant="outline"
							onClick={addCertification}
							className={styles.addButton}
						>
							<Plus className="h-4 w-4 mr-2" />
							Add Another Certification
						</Button>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
